import { defaultLayer, WorkoutLayer } from '@strength/db-schema';
import {
  mergeWorkoutLayers,
  serializeWorkoutToLayer,
} from '@strength/domain-workout';
import { Tag } from '@strength/domain';
import { v4 } from 'uuid';

export type WorkoutTemplateSelection = string[] | 'all';

export type WorkoutTemplateData = {
  id: string;
  name: string;
  template: WorkoutLayer[];
  tags: Tag[];
  // true for multi-day templates: each template entry is one day, stored
  // verbatim. Single-day templates may be a legacy layer stack and are
  // collapsed server-side.
  isMultiDay?: boolean;
};

export function removeInstructionsFromLayers(
  layers: WorkoutLayer[],
): WorkoutLayer[] {
  // for each layer, and for each action that has instructions, remove the instruction
  return layers.map((l) => {
    const entries = Object.entries(l);
    entries.forEach(([_, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (Object.hasOwn(v, 'instruction')) {
            (v as { instruction?: string | null }).instruction = null;
          }
        });
      }
    });
    return l;
  });
}

// Create a new workout template from the layers
// using the selection to filter the layers.
// The template is stored as a single merged layer: the layer stack is
// materialized into one workout and re-serialized as add-only, so the
// merged view is exactly what gets reused on paste.
export function getNewWorkoutTemplate(
  name: string,
  selection: WorkoutTemplateSelection,
  layers: WorkoutLayer[],
): WorkoutTemplateData {
  return {
    id: v4(),
    name,
    template: mergeTemplateLayers(
      getLayersForWorkoutTemplate(selection, layers),
    ),
    tags: [],
  };
}

/**
 * Create a multi-day workout template. Each entry in the template array is one
 * day, materialized to a single merged add-only layer (same as the single-day
 * template, applied per day). The entry's index is the day's position in the
 * selection, so empty days are kept as empty layers to preserve index
 * alignment for apply.
 */
export function getNewMultiWorkoutTemplate(
  name: string,
  workoutIds: string[],
  layersByWorkout: Map<string, WorkoutLayer[]>,
): WorkoutTemplateData {
  return {
    id: v4(),
    name,
    template: workoutIds.map((workoutId) => {
      const dayLayers = layersByWorkout.get(workoutId) ?? [];
      // an empty day still gets one empty layer so entry index === day index
      if (dayLayers.length === 0) {
        return defaultLayer();
      }
      const merged = mergeTemplateLayers(dayLayers);
      if (!merged[0]) {
        throw new Error('mergeTemplateLayers returned no layer');
      }
      return merged[0];
    }),
    tags: [],
    isMultiDay: true,
  };
}

export type MultiWorkoutTemplateSelection = {
  workoutId: string;
  // 'all' saves the whole day; string[] saves just the highlighted sections
  sectionIds: WorkoutTemplateSelection;
}[];

/**
 * Multi-day template from per-day section selections. Days absent from the
 * selection get an empty layer so entry index === day index.
 */
export function getNewMultiWorkoutSectionTemplate(
  name: string,
  selection: MultiWorkoutTemplateSelection,
  layersByWorkout: Map<string, WorkoutLayer[]>,
): WorkoutTemplateData {
  return {
    id: v4(),
    name,
    template: selection.map(({ workoutId, sectionIds }) => {
      const dayLayers = layersByWorkout.get(workoutId) ?? [];
      if (dayLayers.length === 0 || sectionIds.length === 0) {
        return defaultLayer();
      }
      const merged = mergeTemplateLayers(
        getLayersForWorkoutTemplate(sectionIds, dayLayers),
      );
      if (!merged[0]) {
        throw new Error('mergeTemplateLayers returned no layer');
      }
      return merged[0];
    }),
    tags: [],
    isMultiDay: true,
  };
}

/**
 * Collapses a template's layer stack into one add-only layer. The layer
 * boundaries carry no meaning once saved as a template — every consumer
 * merges all layers into one workout anyway.
 */
export function mergeTemplateLayers(layers: WorkoutLayer[]): WorkoutLayer[] {
  const base = layers[0];
  if (!base) {
    return layers;
  }
  const workout = mergeWorkoutLayers(
    wrapTemplateLayerAsHasWorkoutLayer(base),
    ...layers.slice(1).map(wrapTemplateLayerAsHasWorkoutLayer),
  );
  return [serializeWorkoutToLayer(workout)];
}

export const wrapTemplateLayerAsHasWorkoutLayer = (layer: WorkoutLayer) => {
  return {
    layer,
    workoutId: 'foo', // use a dummy workoutId for the layers to have in common
    id: v4(),
    assignedDate: null,
  };
};

/**
 * Pull out the parts of each layer that refer to the selected sections,
 * and keep instructions depending on the flag.
 */
export function getLayersForWorkoutTemplate(
  selection: WorkoutTemplateSelection,
  layers: WorkoutLayer[],
): WorkoutLayer[] {
  const sectionIds = new Set<string>();
  const exerciseIds = new Set<string>();
  const supersetIds = new Set<string>();
  const supersetExerciseIds = new Set<string>();
  const alternateGroupIds = new Set<string>();
  const alternateExerciseIds = new Set<string>();
  const setIds = new Set<string>();

  return layers.map((l) => {
    // get the sectionAdds and sectionEdits that refer to the selected sections
    const sectionAdds = l.sectionAdds.filter((s) =>
      selection === 'all' ? true : selection.includes(s.workoutSectionId),
    );
    const sectionEdits = l.sectionEdits.filter((s) =>
      selection === 'all' ? true : selection.includes(s.workoutSectionId),
    );

    sectionAdds.forEach((s) => sectionIds.add(s.workoutSectionId));

    // get the supersetAdds and supersetEdits that refer to the selected sections
    const supersetAdds = l.supersetAdds.filter((s) =>
      selection === 'all' ? true : selection.includes(s.workoutSectionId),
    );
    supersetAdds.forEach((s) => supersetIds.add(s.workoutSupersetId));
    const supersetEdits = l.supersetEdits.filter((s) =>
      supersetIds.has(s.workoutSupersetId),
    );

    // get the supersetExerciseAdds and supersetExerciseEdits that refer to the selected sections
    const supersetExerciseAdds = l.supersetExerciseAdds.filter((s) =>
      supersetIds.has(s.workoutSupersetId),
    );
    supersetExerciseAdds.forEach((s) =>
      supersetExerciseIds.add(s.workoutSupersetExerciseId),
    );
    const supersetExerciseEdits = l.supersetExerciseEdits.filter((s) =>
      supersetExerciseIds.has(s.workoutSupersetExerciseId),
    );

    // get the alternateGroupAdds and alternateGroupEdits that refer to the selected sections
    const alternateGroupAdds = l.alternateGroupAdds.filter((s) =>
      sectionIds.has(s.workoutSectionId),
    );
    alternateGroupAdds.forEach((s) =>
      alternateGroupIds.add(s.workoutAlternateGroupId),
    );
    const alternateGroupEdits = l.alternateGroupEdits.filter((s) =>
      alternateGroupIds.has(s.workoutAlternateGroupId),
    );

    // get the alternateExerciseAdds and alternateExerciseEdits that refer to the selected sections
    const alternateExerciseAdds = l.alternateExerciseAdds.filter((s) =>
      alternateGroupIds.has(s.workoutAlternateGroupId),
    );
    alternateExerciseAdds.forEach((s) =>
      alternateExerciseIds.add(s.workoutAlternateExerciseId),
    );
    const alternateExerciseEdits = l.alternateExerciseEdits.filter((s) =>
      alternateExerciseIds.has(s.workoutAlternateExerciseId),
    );

    // get the exerciseAdds and exerciseEdits that refer to the selected sections
    const exerciseAdds = l.exerciseAdds.filter((s) =>
      sectionIds.has(s.workoutSectionId),
    );
    exerciseAdds.forEach((s) => exerciseIds.add(s.workoutExerciseId));
    const exerciseEdits = l.exerciseEdits.filter((s) =>
      exerciseIds.has(s.workoutExerciseId),
    );

    // get the exerciseMetricAdds and exerciseMetricEdits that refer to the selected sections
    const exerciseMetricAdds = l.exerciseMetricAdds.filter((s) =>
      exerciseIds.has(s.workoutExerciseId),
    );

    // get the setAdds and setEdits that refer to the selected sections
    const setAdds = l.setAdds.filter((s) =>
      exerciseIds.has(s.workoutExerciseId),
    );
    setAdds.forEach((s) => setIds.add(s.workoutSetId));
    const setEdits = l.setEdits.filter((s) => setIds.has(s.workoutSetId));

    // get the setMetrics that refer to the selected sections
    const setMetrics = l.setMetrics.filter((s) => setIds.has(s.workoutSetId));

    const ids = [
      ...sectionIds,
      ...supersetIds,
      ...supersetExerciseIds,
      ...alternateGroupIds,
      ...alternateExerciseIds,
      ...exerciseIds,
      ...setIds,
    ];
    const removals = l.removals.filter((r) => ids.includes(r.id));

    return {
      sectionAdds,
      sectionEdits,
      supersetAdds,
      supersetEdits,
      supersetExerciseAdds,
      supersetExerciseEdits,
      alternateGroupAdds,
      alternateGroupEdits,
      alternateExerciseAdds,
      alternateExerciseEdits,
      exerciseAdds,
      exerciseEdits,
      exerciseMetricAdds,
      setAdds,
      setEdits,
      setMetrics,
      removals,
    };
  });
}
